/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */

import { useFloating } from '@floating-ui/react-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useClickAway } from 'react-use';
import Colors from '../Colors';
import DropDownMenu, { DropDownMenuOption } from '../DropDownMenu/DropDownMenu';
import * as Icons from '../Icons';
import * as Styled from './styles';
import MultiSelectDropDown, {
  MultiSelectDropDownOption,
  MultiSelectDropDownProps,
} from '../MultiSelectDropDown/MultiSelectDropDown';
import { DropDownChipContainer } from './styles';

const MAX_NUMBER_OF_SELECTED_OPTIONS_TO_SHOW = 3;

const ChevronDownIconJSX = Icons.getBaseIcon('ChevronDown');

const XIconJSX = Icons.getBaseIcon('X');
const xIcon = <XIconJSX color={Colors.Gray[900]} dimension={20} />;

interface DropDownChipProps {
  dropDownType: 'singleselect' | 'multiselect';
  dropDownOptions: DropDownMenuOption[] | MultiSelectDropDownOption[];
  defaultOption?: string;
  placeHolder?: string;
  placement?: 'top' | 'bottom';
  dropDownAlignment?: 'right' | 'left';
  onChange?: (options: MultiSelectDropDownOption[]) => void;
  disableCrossButton?: boolean;
  prefixLabel?: string;
  useAllLabel?: boolean;
  disabled?: boolean;
  loading?: boolean;
  invalid?: boolean;
  dropdownMaxHeightInVH?: number;
  /** Rendered at the top of a multiselect dropdown (e.g. an include/exclude toggle). */
  dropDownHeader?: ReactNode;
  /** Prefixes the selected options shown on the chip, e.g. 'Excluding:' */
  selectedOptionsPrefix?: string;
  /**
   * See `MultiSelectDropDown`. With `'include-exclude'` the chip lists the included and the
   * excluded options separately, e.g. 'Including: A, B · Excluding: C'.
   */
  selectionMode?: MultiSelectDropDownProps['selectionMode'];
}

const DropDownChip = ({
  defaultOption,
  dropDownType,
  dropDownOptions,
  placeHolder,
  dropDownAlignment = 'right',
  onChange,
  placement,
  disableCrossButton = false,
  prefixLabel,
  useAllLabel = true,
  disabled = false,
  loading = false,
  invalid = false,
  dropdownMaxHeightInVH,
  dropDownHeader,
  selectedOptionsPrefix,
  selectionMode,
}: DropDownChipProps) => {
  const [isDropDownCollapsed, setIsDropDownCollapsed] = useState(true);
  const [singleDropDownSelectedOption, setSingleDropDownSelectedOption] =
    useState(
      defaultOption ??
        (dropDownOptions as DropDownMenuOption[]).filter((o) => o.selected)?.[0]
          ?.label
    );
  const [multiDropDownOptions, setMultiDropDownOptions] = useState(
    dropDownOptions as MultiSelectDropDownOption[]
  );
  const isDisabled = disabled || loading;
  const dropDownChipRef = useRef(null);
  const chevronDownIcon = (
    <ChevronDownIconJSX
      color={isDisabled ? Colors.Gray[300] : Colors.Gray[900]}
      dimension={20}
    />
  );

  const { x, y, reference, floating, strategy } = useFloating({
    placement: `${placement ?? 'bottom'}-${
      dropDownAlignment === 'right' ? 'end' : 'start'
    }`,
  });

  // used to colapse the dropdown if the user clicks outside of it
  useClickAway(dropDownChipRef, () => {
    setIsDropDownCollapsed(true);
  });

  // if the parent component changes the options, the changes are reflected here
  useEffect(() => {
    setSingleDropDownSelectedOption(
      defaultOption ??
        (dropDownOptions as DropDownMenuOption[]).filter((o) => o.selected)?.[0]
          ?.label
    );
  }, [defaultOption, dropDownOptions]);

  // The parent is the source of truth, so its options replace whatever is held locally - but only
  // when they actually differ. Parents routinely rebuild this array on every render, and
  // replacing local state on an identical-but-new array discards a selection in progress.
  // Deliberately not reported back through `onChange` either: echoing a parent-driven update
  // makes it look like the user changed the selection, and the parent then persists whatever this
  // chip happens to be holding, dropping anything it does not know about.
  const lastDropDownOptions = useRef(JSON.stringify(dropDownOptions));

  useEffect(() => {
    const serializedOptions = JSON.stringify(dropDownOptions);

    if (lastDropDownOptions.current === serializedOptions) {
      return;
    }

    lastDropDownOptions.current = serializedOptions;
    setMultiDropDownOptions(dropDownOptions);
  }, [dropDownOptions]);

  // Selections made by the user are applied locally and reported to the parent
  const handleMultiDropDownChange = (options: MultiSelectDropDownOption[]) => {
    setMultiDropDownOptions(options);
    onChange?.(options);
  };

  const clearSingleDropDown = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    e.stopPropagation(); // prevent the dropDown from opening when the selected option is cleared
    if (defaultOption) {
      setSingleDropDownSelectedOption(defaultOption);
    } else {
      setSingleDropDownSelectedOption(undefined);
      onChange?.(
        enhancedDropDownOptions.map((o) => ({ ...o, selected: false }))
      );
    }
  };

  const clearMultiDropDown = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    e.stopPropagation(); // prevent the dropDown from opening when the selected options are cleared
    handleMultiDropDownChange(
      multiDropDownOptions.map((option) => ({
        ...option,
        selected: false,
        ...(selectionMode === 'include-exclude' ? { mode: undefined } : {}),
      }))
    );
  };

  const getIcon = (showChevron: boolean) => {
    if (loading) {
      return (
        <span data-test="dropdown-chip-loading-icon">
          <Icons.MiscIcons.LoadingCircle
            color={Colors.Gray[300]}
            dimension={20}
          />
        </span>
      );
    }

    if (showChevron) {
      return chevronDownIcon;
    }

    return (
      <span
        onClick={(e) =>
          dropDownType === 'multiselect'
            ? clearMultiDropDown(e)
            : clearSingleDropDown(e)
        }
        role="button"
        tabIndex={0}
        aria-hidden="true"
      >
        {xIcon}
      </span>
    );
  };

  const generateSingleDropDownButtonContent = () => (
    <Styled.ButtonContent>
      <Styled.ButtonText>
        {singleDropDownSelectedOption
          ? `${prefixLabel ?? ''}${singleDropDownSelectedOption}`
          : placeHolder}
      </Styled.ButtonText>
      {getIcon(
        defaultOption === singleDropDownSelectedOption ||
          !singleDropDownSelectedOption ||
          disableCrossButton
      )}
    </Styled.ButtonContent>
  );

  const summarizeOptions = (options: MultiSelectDropDownOption[]) => {
    let text = options
      .slice(0, MAX_NUMBER_OF_SELECTED_OPTIONS_TO_SHOW)
      .map((option) => option.label)
      .join(', ');
    if (options.length > MAX_NUMBER_OF_SELECTED_OPTIONS_TO_SHOW) {
      text += `, +${options.length - MAX_NUMBER_OF_SELECTED_OPTIONS_TO_SHOW}`;
    }
    return text;
  };

  const generateIncludeExcludeButtonText = () => {
    const included = multiDropDownOptions.filter(
      (option) => option.mode === 'include'
    );
    const excluded = multiDropDownOptions.filter(
      (option) => option.mode === 'exclude'
    );
    return [
      included.length > 0 ? `Including: ${summarizeOptions(included)}` : '',
      excluded.length > 0 ? `Excluding: ${summarizeOptions(excluded)}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const generateMultiDropDownButtonContent = () => {
    const allOption = multiDropDownOptions
      .filter((option) => option.isAllOption && option.selected)
      .map((option) => option.label)
      .join();

    // Dividers and group headers are decoration only, they never count as a selected option
    const multiDropDownSelectableOptions = multiDropDownOptions.filter(
      (option) => option.type !== 'divider' && !option.isGroupOption
    );

    let selectedOptionsText = '';

    if (selectionMode === 'include-exclude') {
      selectedOptionsText = generateIncludeExcludeButtonText();
    } else if (!allOption || (allOption && !useAllLabel)) {
      const selectedOptions = multiDropDownSelectableOptions.filter(
        (option) => option.selected
      );

      if (prefixLabel && selectedOptions.length > 1) {
        const numberOfSelectedOptions = allOption
          ? selectedOptions.length - 1
          : selectedOptions.length;
        selectedOptionsText = `${prefixLabel}: ${numberOfSelectedOptions} selected`;
      } else {
        selectedOptionsText = selectedOptions
          .slice(0, MAX_NUMBER_OF_SELECTED_OPTIONS_TO_SHOW)
          .map((option) => option.label)
          .join(', ');

        if (selectedOptions.length > MAX_NUMBER_OF_SELECTED_OPTIONS_TO_SHOW) {
          selectedOptionsText += `, +${
            selectedOptions.length - MAX_NUMBER_OF_SELECTED_OPTIONS_TO_SHOW
          }`;
        }
      }

      if (selectedOptionsPrefix && selectedOptionsText) {
        selectedOptionsText = `${selectedOptionsPrefix} ${selectedOptionsText}`;
      }
    }

    const textToShow =
      (useAllLabel ? allOption : undefined) ||
      selectedOptionsText ||
      placeHolder;

    return (
      <Styled.ButtonContent>
        <Styled.ButtonText>{textToShow}</Styled.ButtonText>
        {getIcon(textToShow === allOption || textToShow === placeHolder)}
      </Styled.ButtonContent>
    );
  };

  const enhancedDropDownOptions = dropDownOptions.map(
    (option: DropDownMenuOption) => ({
      ...option,
      onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (option.onClick) option.onClick(event);
        setSingleDropDownSelectedOption(option.label || '');
        setIsDropDownCollapsed(true);
      },
    })
  );

  const getDropDown = () => {
    switch (dropDownType) {
      case 'singleselect':
        return (
          <DropDownMenu
            title=""
            hasInput={false}
            options={enhancedDropDownOptions}
            selected={singleDropDownSelectedOption}
          />
        );
      case 'multiselect':
        return (
          <MultiSelectDropDown
            options={multiDropDownOptions}
            includeContainerCard
            input={{
              leadingIcon: 'Search',
              placeholder: 'Search',
            }}
            onChange={handleMultiDropDownChange}
            maxHeightInVH={dropdownMaxHeightInVH}
            header={dropDownHeader}
            selectionMode={selectionMode}
          />
        );
      default:
        // eslint-disable-next-line no-case-declarations
        const exhaustiveCheck: never = dropDownType;
        return exhaustiveCheck;
    }
  };

  return (
    <span data-test="dropdown-chip-filter" ref={dropDownChipRef}>
      <DropDownChipContainer ref={reference}>
        <Styled.Button
          variant="outlined"
          color={invalid ? 'Error' : 'Gray'}
          size="md"
          disabled={isDisabled}
          onClick={() => setIsDropDownCollapsed((c) => !c)}
          fullWidth={false}
        >
          {dropDownType === 'singleselect'
            ? generateSingleDropDownButtonContent()
            : generateMultiDropDownButtonContent()}
        </Styled.Button>
      </DropDownChipContainer>
      {!isDropDownCollapsed && (
        <Styled.DropDownContainer
          data-test="filter-dropdown-container"
          ref={floating}
          $position={strategy}
          $left={x}
          $top={y}
        >
          {getDropDown()}
        </Styled.DropDownContainer>
      )}
    </span>
  );
};

export default DropDownChip;
export type { DropDownChipProps };
